<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
    :error="control.control.value.errors"
  >
    <h3 :class="control.styles.control.label">{{ control.computedLabel.value }}</h3>
    <p v-if="controlOptions?.description" class="mb-6 font-widget-secondary text-fontSecondary">
      {{ controlOptions.description }}
    </p>
    <div class="widget-select-button">
      <WButton
        v-for="(label, index) in labels"
        :key="String(label)"
        :disabled="!control.control.value.enabled"
        :autofocus="control.appliedOptions.value.focus"
        :data-selected="value === values[index]"
        type="outline"
        :variant="getClientTheme()"
        @click="valueChanged(values[index])"
        >{{ label }}</WButton
      >
    </div>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import ControlWrapper from './ControlWrapper.vue';
  import { useWidgetControl } from '../util';
  import { getClientTheme } from 'src/utils/config';
  import WButton from '../../base/Button.vue';

  type TupleOption = { title: string; value: string | number | boolean } | string | number | boolean;
  interface TupleConfig {
    description?: string;
    title: string;
    options: TupleOption[];
  }

  const props = defineProps(rendererProps<ControlElement>());
  const control = useWidgetControl(useJsonFormsControl(props));
  const controlOptions = computed(() => control.control.value?.schema as TupleConfig);
  const value = ref(control.control.value.data);

  if (
    !(
      'options' in control.control.value.schema &&
      typeof control.control.value.schema.options === 'object' &&
      Array.isArray(control.control.value.schema.options)
    )
  ) {
    throw new Error('must provide options param to option control');
  }

  const options = controlOptions.value.options;

  const labels = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.title;
      }
      return option;
    })
  );

  const values = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.value;
      }
      return option;
    })
  );

  function valueChanged(val: string | number | boolean | { title: string; value: string | number | boolean }) {
    control.onChange(val);
    value.value = val;
  }
</script>

<style scoped></style>
